import { ADDRESS_LIMITS } from '../../core/constants';
import type {
  AddressDraft,
  AddressFormDraft,
  GeoPoint,
} from '../../data/models/address/address.model';
import { clampText, optionalText } from './clamp-text';
import { LATITUDE_LIMIT, LONGITUDE_LIMIT, readCoord } from './coords';

/** Живёт отдельно от `address-form.ts`: импорт из файла компонента утащил бы Signal Forms в чанк потребителя. */

export const EMPTY_ADDRESS_DRAFT: AddressFormDraft = Object.freeze({
  street: '',
  apartment: null,
  landmark: null,
  latitude: null,
  longitude: null,
});

/** Обязательны и улица, и точка: без координат сервер отвергает и адрес, и заказ на доставку. */
export function isValidAddressDraft(draft: AddressFormDraft): boolean {
  return toAddressDraft(draft) !== null;
}

/** `null` — точки (или улицы) нет: такой черновик до сети доходить не должен. */
export function toAddressDraft(form: AddressFormDraft): AddressDraft | null {
  const point = toPoint(form);
  const street = clampText(form.street, ADDRESS_LIMITS.STREET);
  if (point === null || street.length === 0) {
    return null;
  }
  return {
    street,
    apartment: optionalText(form.apartment, ADDRESS_LIMITS.APARTMENT),
    landmark: optionalText(form.landmark, ADDRESS_LIMITS.LANDMARK),
    ...point,
  };
}

/** Пара координат атомарна; `readCoord` — защита в глубину: черновик может приехать с бэкенда. */
export function toPoint(draft: AddressFormDraft): GeoPoint | null {
  const latitude = readCoord(draft.latitude, LATITUDE_LIMIT);
  const longitude = readCoord(draft.longitude, LONGITUDE_LIMIT);
  return latitude !== null && longitude !== null ? { latitude, longitude } : null;
}

/** Любое поле формы — ввод: квартира, набранная до загрузки списка, не уступит место дефолтному адресу. */
export function hasDraftInput(draft: AddressFormDraft): boolean {
  return (
    draft.street.trim().length > 0 ||
    (draft.apartment ?? '').trim().length > 0 ||
    (draft.landmark ?? '').trim().length > 0 ||
    toPoint(draft) !== null
  );
}
